/**
 * The step tree as the designer draws it, and back: pure, so the round trip
 * is testable without a page. The tree stays the one source of truth; the
 * designer's `Definition` is only how it is drawn and edited.
 *
 * - An ordinary step is a task box that says what it does in words: "Send
 *   'Energy Welcome'", "Wait 2 days, then at 10:00 (UK time)". A merge
 *   field reads as its name in brackets, "Call [name]".
 * - A Go to is a task, "Go back to Step 1 (up to 2 more times)".
 * - A "Wait until" followed straight away by an If / else that only looks
 *   at that wait (`wait_matched`, no conditions) is one question, "Did they
 *   click a link containing "broadband"? (waits up to 2 days)", with Yes
 *   (it happened) and No (the time ran out). Not when a Go to jumps to the
 *   If / else, since that skips the wait. Both steps keep their ids.
 * - Any other If / else is a switch with Yes and No.
 * - A Repeat is a container, "Do these steps 3 times".
 * - An End is a task with nothing after it, "Finish: ..." or "Stop: ...".
 * - With `words.trigger`, the first box is the trigger itself ("When a new
 *   lead arrives on Energy Premium, only if ..."), id `_trigger`. It is not
 *   a step: `fromDesigner` leaves it out, and nothing goes before it.
 *
 * Every step carries what it was (`properties.w`, and for a question the
 * If / else as `properties.b`), so `fromDesigner` rebuilds the exact tree,
 * ids and configs untouched. A step dragged in from the toolbox carries
 * `properties.fresh` instead, and comes back as a new step with its
 * defaults (`blankStep`) and a step id like the list's.
 */
import type { BranchedStep, Definition, SequentialStep, Step } from 'sequential-workflow-designer';
import type { RecordType, StepKind, WorkflowStep, WorkflowTrigger } from '../../../api/workflows';
import type { Vocab } from '../../../vocab';
import type { SmartFilter } from '../../SmartFilterEditor';
import { onceWords } from '../sendOnce';
import {
  allSteps, blankStep, cfg, DEFAULT_GO_TO_TIMES, eventWords, locateStep, MIN_REPEAT_TIMES, newStepId, num, stepLabel, str,
  linkClickSentence, triggerLabel, updateStep, waitSentence, waitUntilSentence, withEmailFrom,
} from '../workflowModel';

/** What the designer calls each kind of box (`step.type`). */
export type DesignerType = Exclude<StepKind, 'end'> | 'question' | 'finish' | 'stop' | 'trigger';

/** The trigger's box: first in the chart, never a step. */
export const TRIGGER_ID = '_trigger';
export const TRIGGER_TYPE = 'trigger';

/** What a toolbox item makes: a step kind, or a question (a wait until and its If / else). */
export type FreshKind = StepKind | 'question';

/** One of our steps without its lists, and what those lists were when empty. */
export type Kept = {
  own: Record<string, unknown>;
  /** Its keys in their order, `id` and the lists included, so it comes back exactly as it was. */
  keys: string[];
  /** A list that came empty: left out (`absent`) or `null`. Anything else comes back as a list. */
  empty?: Partial<Record<'yes' | 'no' | 'body', 'absent' | 'null'>>;
};

/** A box's `properties`. */
export type BoxProps = {
  /** The step it stands for (for a question, the wait). */
  w?: Kept;
  /** A question's If / else. */
  b?: Kept & { id: string };
  /** From the toolbox: what to make. */
  fresh?: FreshKind;
  /** From the toolbox, an End: done or exited. */
  outcome?: 'done' | 'exited';
};

export const YES = 'Yes';
export const NO = 'No';

/** The longest a box's words run before they are cut short. */
const MAX_NAME = 90;

export type Words = {
  v: Vocab;
  /** Email template names by id. */
  names?: Map<string, string>;
  /** The trigger in words (`triggerSentence`): drawn as the first box when given. */
  trigger?: string;
};

/** A merge field reads as its name in brackets: "Call [name] about broadband". */
export function plain(text: string): string {
  return text.replace(/\{\{\s*([a-z_]+)[^}]*\}\}/gi, '[$1]');
}

/** "once more", or "up to 3 more times". */
export function timesLabel(n: number): string {
  return n === 1 ? 'once more' : `up to ${n} more times`;
}

/** "2 days", "1 hour": how long a wait until waits. */
export function waitLength(step: WorkflowStep): string {
  const c = cfg(step);
  const amount = Math.trunc(num(c.amount));
  const unit = str(c.unit) || 'days';
  return `${amount} ${amount === 1 ? unit.replace(/s$/, '') : unit}`;
}

/** A step as its box says it. */
export function describeStep(
  v: Vocab, s: WorkflowStep, d: { names?: Map<string, string>; numbers?: Map<string, number> } = {},
): string {
  const c = cfg(s);
  switch (s.kind) {
    case 'send_email': {
      const name = d.names?.get(str(c.template_id));
      return name ? `Send '${name}'` : 'Send an email';
    }
    case 'notify': {
      const who = c.to === 'owner' ? `the ${v.one('owner')}` : c.to === 'team_lead' ? `their ${v.one('team')}'s leader` : 'someone';
      return str(c.title).trim() ? `Tell ${who}: ${plain(str(c.title))}` : `Tell ${who}`;
    }
    case 'create_task': return str(c.subject).trim() ? `${v.One('task')}: ${plain(str(c.subject))}` : stepLabel(v, s.kind);
    case 'set_property': return str(c.key) ? `Set ${str(c.key)}` : stepLabel(v, s.kind);
    case 'wait': return waitSentence(c);
    case 'wait_until': return waitUntilSentence(v, c);
    case 'end': {
      const reason = str(c.reason).trim();
      const word = c.outcome === 'exited' ? 'Stop' : 'Finish';
      return reason ? `${word}: ${reason}` : word;
    }
    case 'go_to': {
      const n = d.numbers?.get(str(c.target));
      if (!n) return 'Go back to: choose a step';
      return `Go back to Step ${n} (${timesLabel(Math.trunc(num(c.max_times, DEFAULT_GO_TO_TIMES)))})`;
    }
    case 'repeat': return `Do these steps ${Math.trunc(num(c.times, MIN_REPEAT_TIMES))} times`;
    default: return stepLabel(v, s.kind);
  }
}

/**
 * A wait asked as a question: "Did they click a link containing "broadband"?",
 * or "The deal is won?" for what is not something they do.
 */
export function askWait(v: Vocab, w: WorkflowStep): string {
  const ev = cfg(w).event as WorkflowTrigger | null | undefined;
  if (!ev || typeof ev !== 'object') return 'Do the conditions come true?';
  const words = eventWords(v, ev);
  return words.startsWith('they ') ? `Did ${words}?` : `${words.replace(/^./, (c) => c.toUpperCase())}?`;
}

function cut(text: string): string {
  return text.length > MAX_NAME ? `${text.slice(0, MAX_NAME - 1).trimEnd()}…` : text;
}

/** Whether an If / else only looks at the wait before it, so the two are one question. */
export function decidesWait(branch: WorkflowStep): boolean {
  const c = cfg(branch);
  const conds = Array.isArray(c.conditions) ? c.conditions : [];
  return branch.kind === 'branch' && conds.length === 0 && typeof c.wait_matched === 'boolean';
}

/** Whether a box is the trigger's, which is not a step. */
export function isTrigger(step: Pick<Step, 'type'> | undefined): boolean {
  return step?.type === TRIGGER_TYPE;
}

/** Whether a box is an End, with nothing after it. */
export function isEnd(step: Pick<Step, 'type'>): boolean {
  return step.type === 'finish' || step.type === 'stop';
}

function keep(s: WorkflowStep, sides: ('yes' | 'no' | 'body')[]): Kept {
  const { id: _id, ...rest } = s;
  void _id;
  const own: Record<string, unknown> = { ...rest };
  const empty: Kept['empty'] = {};
  for (const side of sides) {
    const list = s[side];
    delete own[side];
    if (list === undefined) empty[side] = 'absent';
    else if (list === null) empty[side] = 'null';
  }
  const keys = Object.keys(s);
  return Object.keys(empty).length ? { own, keys, empty } : { own, keys };
}

/** A step put back together from what it kept, its keys in their old order. */
function assemble(id: string, k: Kept, lists: Partial<Record<'yes' | 'no' | 'body', WorkflowStep[]>> = {}): WorkflowStep {
  const parts: Record<string, unknown> = { id, ...k.own };
  for (const [key, list] of Object.entries(lists) as ['yes' | 'no' | 'body', WorkflowStep[]][]) {
    Object.assign(parts, side(list, k, key));
  }
  const out: Record<string, unknown> = {};
  for (const key of k.keys) if (key in parts) out[key] = parts[key];
  for (const key of Object.keys(parts)) if (!(key in out)) out[key] = parts[key];
  return out as WorkflowStep;
}

/** The steps as the designer's definition. */
export function toDesigner(steps: WorkflowStep[], words: Words): Definition {
  const { v, names } = words;
  const all = allSteps(steps);
  const numbers = new Map(all.map((s, i) => [s.id, i + 1]));
  const goToTargets = new Set(all.filter((s) => s.kind === 'go_to').map((s) => str(cfg(s).target)));

  const seq = (list: WorkflowStep[]): Step[] => {
    const out: Step[] = [];
    for (let i = 0; i < list.length; i += 1) {
      const s = list[i]!;
      const next = list[i + 1];
      if (s.kind === 'wait_until' && next && decidesWait(next) && !goToTargets.has(next.id)) {
        const matched = cfg(next).wait_matched === true ? 'yes' : 'no';
        const timed = matched === 'yes' ? 'no' : 'yes';
        const props: BoxProps = { w: keep(s, []), b: { id: next.id, ...keep(next, ['yes', 'no']) } };
        out.push({
          id: s.id,
          componentType: 'switch',
          type: 'question',
          name: cut(`${askWait(v, s)} (waits up to ${waitLength(s)})`),
          properties: props,
          branches: { [YES]: seq(next[matched] ?? []), [NO]: seq(next[timed] ?? []) },
        } as BranchedStep);
        i += 1;
        continue;
      }
      const name = cut(describeStep(v, s, { names, numbers }));
      if (s.kind === 'branch') {
        out.push({
          id: s.id, componentType: 'switch', type: 'branch', name, properties: { w: keep(s, ['yes', 'no']) },
          branches: { [YES]: seq(s.yes ?? []), [NO]: seq(s.no ?? []) },
        } as BranchedStep);
      } else if (s.kind === 'repeat') {
        out.push({
          id: s.id, componentType: 'container', type: 'repeat', name, properties: { w: keep(s, ['body']) },
          sequence: seq(s.body ?? []),
        } as SequentialStep);
      } else {
        const type: DesignerType = s.kind === 'end' ? (cfg(s).outcome === 'exited' ? 'stop' : 'finish') : s.kind;
        out.push({ id: s.id, componentType: 'task', type, name, properties: { w: keep(s, []) } });
      }
    }
    return out;
  };
  const sequence = seq(steps);
  if (words.trigger) {
    sequence.unshift({ id: TRIGGER_ID, componentType: 'task', type: TRIGGER_TYPE, name: words.trigger, properties: {} });
  }
  return { properties: {}, sequence };
}

/** A list from a box's sequence, left out or null when it came that way and is still empty. */
function side(list: WorkflowStep[], k: Kept, key: 'yes' | 'no' | 'body'): Partial<WorkflowStep> {
  if (list.length === 0 && k.empty?.[key] === 'absent') return {};
  if (list.length === 0 && k.empty?.[key] === 'null') return { [key]: null } as Partial<WorkflowStep>;
  return { [key]: list } as Partial<WorkflowStep>;
}

export type FromDesigner = {
  steps: WorkflowStep[];
  /** New steps' ids: the designer's id to the step id they were given (a question's is its wait's). */
  renamed: Map<string, string>;
};

/** The designer's definition as steps: the same tree, with new toolbox steps made in full. */
export function fromDesigner(def: Definition, rt: RecordType): FromDesigner {
  /** New steps, by the temporary id they carry until they get their own. */
  const fresh: { tmp: string; designerId: string }[] = [];

  const seq = (list: Step[]): WorkflowStep[] => list.flatMap((box): WorkflowStep[] => {
    if (isTrigger(box)) return [];
    const p = (box.properties ?? {}) as BoxProps;
    const branches = (box as BranchedStep).branches ?? {};
    const yes = seq(branches[YES] ?? []);
    const no = seq(branches[NO] ?? []);
    const body = seq((box as SequentialStep).sequence ?? []);
    if (p.fresh) {
      if (p.fresh === 'question') {
        const wait = blankStep('wait_until', [], rt);
        const branch = blankStep('branch', [], rt);
        fresh.push({ tmp: box.id, designerId: box.id }, { tmp: `${box.id}:b`, designerId: '' });
        return [
          { ...wait, id: box.id },
          { ...branch, id: `${box.id}:b`, yes, no },
        ];
      }
      const made = blankStep(p.fresh, [], rt);
      fresh.push({ tmp: box.id, designerId: box.id });
      const step: WorkflowStep = { ...made, id: box.id };
      if (p.fresh === 'end' && p.outcome) step.config = { ...cfg(made), outcome: p.outcome };
      if (p.fresh === 'branch') return [{ ...step, yes, no }];
      if (p.fresh === 'repeat') return [{ ...step, body }];
      return [step];
    }
    const w = p.w ?? { own: { kind: 'wait', config: {} }, keys: [] };
    if (box.type === 'question' && p.b) {
      const { id: bid, ...b } = p.b;
      const matched = cfg({ config: b.own.config } as WorkflowStep).wait_matched === true;
      return [assemble(box.id, w), assemble(bid, b, { yes: matched ? yes : no, no: matched ? no : yes })];
    }
    if (box.componentType === 'switch') return [assemble(box.id, w, { yes, no })];
    if (box.componentType === 'container') return [assemble(box.id, w, { body })];
    return [assemble(box.id, w)];
  });

  let steps = seq(def.sequence);
  const renamed = new Map<string, string>();
  // Numbered as the list numbers a new step, counting only steps that have their own ids.
  const waiting = new Set(fresh.map((f) => f.tmp));
  for (const f of fresh) {
    const id = newStepId(allSteps(steps).filter((s) => !waiting.has(s.id)).map((s) => ({ id: s.id })));
    steps = updateStep(steps, f.tmp, (s) => ({ ...s, id }));
    waiting.delete(f.tmp);
    if (f.designerId) renamed.set(f.designerId, id);
  }
  // A new wait listens to the email sent before it, as one added in the list does.
  for (const id of renamed.values()) {
    const found = locateStep(steps, id);
    const step = found && allSteps(steps).find((s) => s.id === id);
    if (found && step) steps = updateStep(steps, id, () => withEmailFrom(steps, found.at, found.index, step));
  }
  return { steps, renamed };
}

/**
 * Validation problems by step id. `stepProblems` says them as "Step 3: ...",
 * numbered over every step in written order; anything not about one step
 * (branches nested too deep) comes back under "".
 */
export function problemsByStep(messages: string[], all: WorkflowStep[]): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const m of messages) {
    const hit = /^Step (\d+)\b[:]?\s*/.exec(m);
    const id = hit ? (all[Number(hit[1]) - 1]?.id ?? '') : '';
    const text = hit ? m.slice(hit[0].length).replace(/^./, (c) => c.toUpperCase()) : m;
    out.set(id, [...(out.get(id) ?? []), text]);
  }
  return out;
}

/** The step ids a box stands for: one, or a question's wait and If / else. */
export function stepIdsOf(box: Step): string[] {
  const p = (box.properties ?? {}) as BoxProps;
  return p.b ? [box.id, p.b.id] : [box.id];
}

/** Every box in a definition, depth first. */
export function allBoxes(list: Step[]): Step[] {
  return list.flatMap((b) => [
    b,
    ...Object.values((b as BranchedStep).branches ?? {}).flatMap(allBoxes),
    ...allBoxes((b as SequentialStep).sequence ?? []),
  ]);
}

/** Where a sequence sits: how many branches and repeats hold it, and whether a repeat does. */
export function placeOf(def: Definition, target: Step[]): { depth: number; inRepeat: boolean } | null {
  const walk = (list: Step[], depth: number, inRepeat: boolean): { depth: number; inRepeat: boolean } | null => {
    if (list === target) return { depth, inRepeat };
    for (const b of list) {
      for (const inner of Object.values((b as BranchedStep).branches ?? {})) {
        const r = walk(inner, depth + 1, inRepeat);
        if (r) return r;
      }
      const body = (b as SequentialStep).sequence;
      if (body) {
        const r = walk(body, depth + 1, inRepeat || b.type === 'repeat');
        if (r) return r;
      }
    }
    return null;
  };
  return walk(def.sequence, 0, false);
}

/** How many levels a box brings with it: 1 for an If / else, question or Repeat, more with others inside. */
export function levelsOf(box: Pick<Step, 'componentType'> & Partial<BranchedStep & SequentialStep>): number {
  if (box.componentType !== 'switch' && box.componentType !== 'container') return 0;
  const inner = [...Object.values(box.branches ?? {}), box.sequence ?? []];
  return 1 + Math.max(0, ...inner.flatMap((list) => list.map((b) => levelsOf(b))));
}

/** Whether a box is, or holds, a Repeat. */
export function holdsRepeat(box: Step): boolean {
  return allBoxes([box]).some((b) => b.type === 'repeat');
}

// ---------------------------------------------------------------------------
// The trigger's box, in words
// ---------------------------------------------------------------------------

const OPERATOR_WORDS: Record<string, string> = {
  equals: 'is', not_equals: 'is not', contains: 'contains', not_contains: 'does not contain', starts_with: 'starts with',
  ends_with: 'ends with', in: 'is', any_of: 'is', not_in: 'is not', greater_than: 'is more than', gt: 'is more than',
  gte: 'is at least', less_than: 'is less than', lt: 'is less than', lte: 'is at most',
};

/** One condition in a few words: "source is energy-form or utility-switch-form". */
export function conditionWords(f: SmartFilter): string {
  const c = f.config ?? {};
  const what = (c.field ?? '').trim() || f.type.replace(/_/g, ' ');
  const op = c.operator ?? '';
  if (op === 'is_empty') return `${what} is empty`;
  if (op === 'is_not_empty') return `${what} is not empty`;
  const values = (c.value ?? '').split(',').map((x) => x.trim()).filter(Boolean);
  const joined = values.length > 1 ? `${values.slice(0, -1).join(', ')} or ${values[values.length - 1]}` : (values[0] ?? '');
  if (!joined) return what;
  return `${what} ${OPERATOR_WORDS[op] ?? 'matches'} ${joined}`;
}

/** The conditions as a clause: "only if postcode is SW1A 1AA and 1 more". Empty with none. */
export function conditionsClause(andFilters: SmartFilter[], orFilters: SmartFilter[]): string {
  const and = andFilters.filter((f) => f.enabled !== false);
  const or = orFilters.filter((f) => f.enabled !== false);
  const first = and[0] ?? or[0];
  if (!first) return '';
  const rest = and.length + or.length - 1;
  const joiner = and.length > 0 ? 'and' : 'or';
  return `only if ${conditionWords(first)}${rest > 0 ? ` ${joiner} ${rest} more` : ''}`;
}

/** A schedule as the trigger's box says it: "Once at Wed 28 Oct, 10:00 to: Swicc customers". */
export type ScheduleWords = { frequency: string; at?: string | null; sentence: string; segment_name: string };

/** "When a new lead arrives on Energy Premium", "When a deal is won". */
export function whenWords(v: Vocab, t: WorkflowTrigger, names: (id: string) => string | undefined): string {
  const many = (ids: string[]) => {
    const shown = ids.map((id) => names(id) ?? 'a stream');
    return shown.length > 2 ? `${shown[0]} or ${shown.length - 1} more` : shown.join(' or ');
  };
  switch (t.kind) {
    case 'submission_arrived': {
      const ids = t.campaign_ids ?? [];
      return ids.length ? `When a new ${v.one('lead')} arrives on ${many(ids)}` : `When a new ${v.one('lead')} arrives`;
    }
    case 'email_link_clicked': {
      const tpl = t.email_template_id ? names(t.email_template_id) : null;
      return linkClickSentence(t, tpl);
    }
    case 'enrolment': return `When ${v.many(t.record_type === 'submission' ? 'lead' : 'person')} are sent from a segment`;
    default: {
      const label = triggerLabel(v, t.kind);
      return `When ${label.replace(/^./, (x) => x.toLowerCase())}`;
    }
  }
}

/**
 * The trigger's box: when it starts, then its conditions or, sent from a
 * segment, its next send. Cut to fit, the "when" kept whole if it can be.
 */
export function triggerSentence(
  v: Vocab, t: WorkflowTrigger, names: (id: string) => string | undefined,
  andFilters: SmartFilter[], orFilters: SmartFilter[], schedule?: ScheduleWords | null,
): string {
  let when = whenWords(v, t, names);
  if (t.kind === 'enrolment' && schedule) {
    const at = schedule.frequency === 'once' && schedule.at ? `Once at ${onceWords(schedule.at)}` : schedule.sentence.replace(/\.$/, '');
    when = `${at} to: ${schedule.segment_name}`;
  }
  const extra = conditionsClause(andFilters, orFilters);
  if (!extra) return cut(when);
  if (when.length >= MAX_NAME - 12) return cut(when);
  return cut(`${when}, ${extra}`);
}
